'use client';

import { useCallback } from 'react';
import type { TranslationsMap } from '../i18n/types';
import { clampLanguage, useLanguage } from '../stores/languageStore';

type InterpolationValues = Record<string, string | number>;

export function useLocalTranslation<T extends Record<string, string>>(
  translations: TranslationsMap<T>
) {
  const lang = clampLanguage(useLanguage());

  const currentTranslations = translations[lang];

  const t = useCallback(
    <K extends keyof T>(key: K, params?: InterpolationValues): string => {
      const value = currentTranslations[key];

      if (typeof value !== 'string') {
        console.warn(`Translation value is not a string: ${String(key)}`);
        return String(key);
      }

      if (params) {
        return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) =>
          String(params[paramKey] ?? `{{${paramKey}}}`)
        );
      }

      return value;
    },
    [currentTranslations]
  );

  return { t, lang };
}
